import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';
import { DateInput } from './reports/DateInput';

const API_BASE = '/api/coolers';

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'ACTIVE', label: 'Aktif' },
  { key: 'FAULTY', label: 'Arızalı' },
  { key: 'INACTIVE', label: 'Pasif' },
  { key: 'REMOVED', label: 'Sökülmüş' },
];

const STATUS_META = {
  ACTIVE: { label: 'Aktif', tone: 'ok' },
  FAULTY: { label: 'Arızalı', tone: 'bad' },
  INACTIVE: { label: 'Pasif', tone: 'neutral' },
  REMOVED: { label: 'Sökülmüş', tone: 'neutral' },
};

const PAGE_SIZE_DEFAULT = 20;

const EMPTY_FORM = {
  textId: '',
  customerId: '',
  customerTitle: '',
  barcode: '',
  brand: '',
  model: '',
  doorCount: 1,
  status: 'ACTIVE',
  installedAt: '',
  lastServiceAt: '',
  temperatureCelsius: '',
  photoUrl: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    activeCount: pick(data, 'activeCount', 'ActiveCount') ?? 0,
    faultyCount: pick(data, 'faultyCount', 'FaultyCount') ?? 0,
    inactiveCount: pick(data, 'inactiveCount', 'InactiveCount') ?? 0,
    removedCount: pick(data, 'removedCount', 'RemovedCount') ?? 0,
  };
}

function statusPillFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  const tones = {
    ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
    bad: { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' },
    neutral: { color: '#64748b', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
  };
  const t = tones[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

function temperaturePill(c) {
  if (c == null || c === '') return <span style={{ color: '#94a3b8' }}>-</span>;
  const n = Number(c);
  if (!Number.isFinite(n)) return <span>-</span>;
  // 2-8°C ideal (gıda dolabı). Dışında uyarı.
  const ok = n >= 2 && n <= 8;
  const tone = ok
    ? { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' }
    : { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 999, fontWeight: 700, fontSize: 12,
      background: tone.background, border: `1px solid ${tone.borderColor}`, color: tone.color,
    }}>
      {n.toFixed(1)}°C
    </span>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '-'; }
}

function toLocalDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
}

function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}

function badge(label, value, tone) {
  const tones = {
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function CoolersPageLegacy() {
  const [statusFilter, setStatusFilter] = useState('');
  const [barcodeFilter, setBarcodeFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ activeCount: 0, faultyCount: 0, inactiveCount: 0, removedCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (barcodeFilter.trim()) params.set('barcode', barcodeFilter.trim());
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        activeCount: data.activeCount,
        faultyCount: data.faultyCount,
        inactiveCount: data.inactiveCount,
        removedCount: data.removedCount,
      });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, barcodeFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing({ mode: 'create' }); setForm(EMPTY_FORM); };

  const openEdit = (row) => {
    setEditing({ mode: 'edit', id: pick(row, 'id', 'Id') });
    setForm({
      textId: pick(row, 'textId', 'TextId') || '',
      customerId: pick(row, 'customerId', 'CustomerId') || '',
      customerTitle: pick(row, 'customerTitle', 'CustomerTitle') || '',
      barcode: pick(row, 'barcode', 'Barcode') || '',
      brand: pick(row, 'brand', 'Brand') || '',
      model: pick(row, 'model', 'Model') || '',
      doorCount: pick(row, 'doorCount', 'DoorCount') ?? 1,
      status: pick(row, 'status', 'Status') || 'ACTIVE',
      installedAt: toLocalDateInput(pick(row, 'installedAt', 'InstalledAt')),
      lastServiceAt: toLocalDateInput(pick(row, 'lastServiceAt', 'LastServiceAt')),
      temperatureCelsius: pick(row, 'temperatureCelsius', 'TemperatureCelsius') ?? '',
      photoUrl: pick(row, 'photoUrl', 'PhotoUrl') || '',
    });
  };

  const closeEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submitForm = async () => {
    if (!form.customerId.trim()) { showToast('error', 'CustomerId zorunlu.'); return; }
    if (!form.barcode.trim()) { showToast('error', 'Barkod zorunlu.'); return; }

    setSaving(true);
    try {
      const body = {
        textId: form.textId.trim() || null,
        customerId: form.customerId.trim(),
        barcode: form.barcode.trim(),
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        doorCount: Number(form.doorCount) || 0,
        status: form.status,
        installedAt: fromLocalDateInput(form.installedAt),
        lastServiceAt: fromLocalDateInput(form.lastServiceAt),
        temperatureCelsius: form.temperatureCelsius === '' ? null : Number(form.temperatureCelsius),
        photoUrl: form.photoUrl.trim() || null,
      };
      const url = editing.mode === 'edit' ? `${API_BASE}/${editing.id}` : API_BASE;
      const method = editing.mode === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', editing.mode === 'edit' ? 'Cooler güncellendi.' : 'Cooler oluşturuldu.');
      closeEdit();
      await load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteRow) return;
    const id = pick(deleteRow, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Cooler silindi.');
      setDeleteRow(null);
      await load();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    } finally {
      setDeleteBusy(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'barcode', 'Barcode'),
        pick(row, 'brand', 'Brand'),
        pick(row, 'model', 'Model'),
        pick(row, 'customerTitle', 'CustomerTitle'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Barcode': return pick(row, 'barcode', 'Barcode') || '';
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'Status': return pick(row, 'status', 'Status') || '';
        case 'Brand': return pick(row, 'brand', 'Brand') || '';
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'barcode',
      headerName: 'Barkod',
      flex: 0.9,
      minWidth: 140,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>
            {pick(row, 'barcode', 'Barcode') || '-'}
          </span>
          {pick(row, 'textId', 'TextId') && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>{pick(row, 'textId', 'TextId')}</span>
          )}
        </div>
      ),
    },
    {
      field: 'customerTitle',
      headerName: 'Nokta',
      flex: 1.3,
      minWidth: 180,
      valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '(Müşteri yok)',
    },
    {
      field: 'brand',
      headerName: 'Marka / Model',
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 600 }}>{pick(row, 'brand', 'Brand') || '-'}</span>
          {pick(row, 'model', 'Model') && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>{pick(row, 'model', 'Model')}</span>
          )}
        </div>
      ),
    },
    {
      field: 'doorCount',
      headerName: 'Kapı',
      flex: 0.4,
      minWidth: 70,
      valueGetter: ({ row }) => pick(row, 'doorCount', 'DoorCount') ?? 0,
    },
    {
      field: 'temperatureCelsius',
      headerName: 'Sıcaklık',
      flex: 0.5,
      minWidth: 100,
      renderCell: ({ row }) => temperaturePill(pick(row, 'temperatureCelsius', 'TemperatureCelsius')),
    },
    {
      field: 'lastServiceAt',
      headerName: 'Son Servis',
      flex: 0.7,
      minWidth: 110,
      valueGetter: ({ row }) => formatDate(pick(row, 'lastServiceAt', 'LastServiceAt')),
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.7,
      minWidth: 130,
      renderCell: ({ row }) => statusPillFor(pick(row, 'status', 'Status')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.9,
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--warn)' }}
            onClick={() => openEdit(row)}
          >
            Düzenle
          </button>
          <button
            type="button"
            style={{ ...gridActionButton, background: '#dc2626' }}
            onClick={() => setDeleteRow(row)}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Soğutucular</h1>
          <p style={s.pageSubtitle}>
            Saha noktalarındaki buzdolabı / soğutucu master kayıtları. Barkod gerçek
            dünya tanımlayıcısı; arıza bildirimleri bu barkod üzerinden bağlanır.
            Mobile master delta ile çeker.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Aktif', counts.activeCount, 'green')}
          {badge('Arızalı', counts.faultyCount, 'red')}
          {badge('Pasif', counts.inactiveCount, 'gray')}
          {badge('Sökülmüş', counts.removedCount, 'gray')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
          <button type="button" style={s.btnPrimary} onClick={openCreate}>
            + Yeni Cooler
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              style={{
                ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="text"
            value={barcodeFilter}
            onChange={(e) => setBarcodeFilter(e.target.value)}
            placeholder="Tam barkod (server-filter)"
            style={{ ...s.filterInput, width: 200, fontFamily: 'monospace', fontSize: 12 }}
          />
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Barkod / marka / nokta..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openEdit}
        exportFileName="coolerlar"
      />

      {editing && (
        <div style={s.overlay} onClick={closeEdit}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {editing.mode === 'edit' ? 'Cooler Düzenle' : 'Yeni Cooler'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeEdit}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Müşteri ID (GUID) *</label>
                  <input
                    type="text"
                    value={form.customerId}
                    onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    style={{ ...s.input, fontFamily: 'monospace', fontSize: 12 }}
                  />
                  {form.customerTitle && (
                    <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>
                      ✓ {form.customerTitle}
                    </div>
                  )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum *</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    style={s.select}
                  >
                    {STATUS_TABS.filter(t => t.key).map(t => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Barkod *</label>
                  <input
                    type="text"
                    value={form.barcode}
                    onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                    placeholder="örn. CLR-2026-00184"
                    style={{ ...s.input, fontFamily: 'monospace' }}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>TextId</label>
                  <input
                    type="text"
                    value={form.textId}
                    onChange={(e) => setForm({ ...form, textId: e.target.value })}
                    style={s.input}
                  />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Marka</label>
                  <input
                    type="text"
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Model</label>
                  <input
                    type="text"
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Kapı Sayısı</label>
                  <input
                    type="number"
                    min="0"
                    value={form.doorCount}
                    onChange={(e) => setForm({ ...form, doorCount: e.target.value })}
                    style={s.input}
                  />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kurulum Tarihi</label>
                  <DateInput value={form.installedAt} onChange={(v) => setForm({ ...form, installedAt: v })} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Son Servis Tarihi</label>
                  <DateInput value={form.lastServiceAt} onChange={(v) => setForm({ ...form, lastServiceAt: v })} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Sıcaklık (°C)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={form.temperatureCelsius}
                    onChange={(e) => setForm({ ...form, temperatureCelsius: e.target.value })}
                    placeholder="(opsiyonel)"
                    style={s.input}
                  />
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Fotoğraf URL</label>
                <input
                  type="text"
                  value={form.photoUrl}
                  onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
                  placeholder="https://..."
                  style={s.input}
                />
                {form.photoUrl && (
                  <img
                    src={form.photoUrl}
                    alt="cooler"
                    style={{ marginTop: 8, maxWidth: 200, maxHeight: 150, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeEdit} disabled={saving}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={submitForm}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : (editing.mode === 'edit' ? 'Güncelle' : 'Oluştur')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteRow && (
        <div style={s.overlay} onClick={() => setDeleteRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Cooler Sil</h2>
              <button type="button" style={s.closeBtn} onClick={() => setDeleteRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 14, color: '#1f2937' }}>
                <strong>{pick(deleteRow, 'barcode', 'Barcode')}</strong> coolerını silmek istediğinize emin misiniz?
              </div>
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
                Bağlı arıza bildirimleri (CoolerFaultReport) etkilenmez (string barkod ile bağlı).
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDeleteRow(null)} disabled={deleteBusy}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }}
                onClick={submitDelete}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CoolersPageLegacy;
